import { FC, useState } from 'react';
import { Navigate, useLocation, useSearchParams } from 'react-router-dom';
import { Alert, Button, Heading, Icon, Paragraph } from '@centrix/shared-ui';
import {
  useMiscSectorsList,
  useOnboardingStatus,
  useSubmitOnboardingSectors,
} from '@centrix/api-client';
import { getMfeRouteUrl, SECTOR_LABELS } from '@centrix/types';

import logoImage from '@/assets/logo.png';
import {
  peopleIcon,
  buildingIcon,
  personIcon,
  helpIcon,
  institutionIcon,
} from '@centrix/shared-ui/src/icons';
import {
  isEmailMarkedVerified,
  resolveOnboardingEmail,
  resolveOnboardingToken,
} from './onboarding';

interface Sector {
  id: string;
  label: string;
  icon: string;
}

const FALLBACK_ICON_MAP: Record<string, string> = {
  ifa: peopleIcon,
  ich: buildingIcon,
  gcs: personIcon,
  isa: helpIcon,
  la: institutionIcon,
};

const DEFAULT_ICON = personIcon;

const FALLBACK_SECTORS: Sector[] = [
  { id: 'ifa', label: 'Independent Fostering Agency', icon: peopleIcon },
  { id: 'ich', label: "Independent Children's Home", icon: buildingIcon },
  { id: 'gcs', label: "General Children's Services", icon: personIcon },
  { id: 'isa', label: 'Independent Supported Accommodation', icon: helpIcon },
  { id: 'la', label: 'Local Authority', icon: institutionIcon },
];

interface SectorCardProps {
  sector: Sector;
  selected: boolean;
  onSelect: (sectorId: string) => void;
}

const SectorCard: FC<SectorCardProps> = ({ sector, selected, onSelect }) => {
  const handleClick = () => onSelect(sector.id);
  const cardStateClass = selected
    ? 'bg-brand-panel border-brand-primary hover:bg-brand-panel'
    : 'bg-white border-transparent hover:bg-white';
  const iconWellClass = selected
    ? 'bg-brand-primary'
    : 'bg-brand-tint group-hover:bg-brand-primary';
  const iconColorClass = selected ? 'text-white' : 'text-brand-primary group-hover:text-white';

  return (
    <Button
      type="button"
      variant="sectorCard"
      size="sectorCard"
      onClick={handleClick}
      aria-pressed={selected}
      className={`group ${cardStateClass}`}
    >
      <div
        className={`flex size-[50px] shrink-0 items-center justify-center rounded-[10px] transition-colors ${iconWellClass}`}
      >
        <Icon src={sector.icon} className={`size-[34px] transition-colors ${iconColorClass}`} />
      </div>
      <Paragraph className="text-center text-lg font-medium leading-6 text-black">
        {sector.label}
      </Paragraph>
    </Button>
  );
};

export const SelectSectorsPage: FC = () => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [error, setError] = useState<string | null>(null);
  const token = resolveOnboardingToken({
    locationState: location.state,
    searchParams,
  });
  const [selected, setSelected] = useState<Set<string>>(
    () => new Set(searchParams.getAll('sector').filter(Boolean))
  );

  const { data: onboardingStatus } = useOnboardingStatus(token ?? undefined);
  const isIndividual = onboardingStatus?.registration_type === 'individual';

  // The Sector Selection screen is only reachable after a successful OTP
  // verification in this tab. If the email is not marked verified (direct URL
  // visit, or browser Forward after the verified session was cleared by
  // returning to step 1), send the user back to the start of signup so they
  // must re-verify rather than picking up the secure flow mid-way.
  const onboardingEmail = resolveOnboardingEmail({
    locationState: location.state,
    searchParams,
    currentUser: null,
  });
  const isVerified = isEmailMarkedVerified(onboardingEmail);

  const { data: sectorsData, isLoading } = useMiscSectorsList();
  const submitSectors = useSubmitOnboardingSectors(token || null);

  const sectors: Sector[] =
    !isLoading && sectorsData?.results?.length
      ? sectorsData.results.map((sector) => ({
          id: sector.slug,
          label: SECTOR_LABELS[sector.slug] ?? sector.name,
          icon: FALLBACK_ICON_MAP[sector.slug] ?? DEFAULT_ICON,
        }))
      : FALLBACK_SECTORS;

  const toggle = (id: string) => {
    setError(null);
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleContinue = async () => {
    if (selected.size === 0) {
      return;
    }

    const selectedSectors = Array.from(selected);
    setError(null);

    try {
      await submitSectors.mutateAsync({ sectors: selectedSectors });
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : 'Failed to save your sector selection. Please try again.'
      );
      return;
    }

    const commerceUrl = new URL(
      getMfeRouteUrl('commerce', isIndividual ? 'individual-courses' : 'choose-subscription', {
        port: 3003,
        env: import.meta.env,
        isDev: import.meta.env.DEV,
      }),
      window.location.origin || 'http://localhost'
    );

    selectedSectors.forEach((sector) => commerceUrl.searchParams.append('sector', sector));
    if (token) {
      commerceUrl.searchParams.set('token', token);
    }

    window.location.href = commerceUrl.toString();
  };

  const isContinuing = submitSectors.isPending;

  if (!isVerified) {
    return <Navigate to="/signup" replace />;
  }

  return (
    <div className="min-h-screen bg-white flex flex-col items-center justify-center py-12 px-4">
      <div className="mb-6">
        <img
          src={logoImage}
          alt="Learnovate360"
          width={244}
          height={115}
          className="object-contain"
        />
      </div>

      <div className="text-center mb-6 flex flex-col gap-2 items-center">
        <Heading as="h1" className="text-[32px] font-medium text-black leading-9">
          Select Sectors
        </Heading>
        <Paragraph className="text-sm text-gray-body leading-6">
          Please select sectors to get started
        </Paragraph>
      </div>

      {error ? (
        <div className="mb-6 w-full max-w-3xl">
          <Alert variant="destructive" onDismiss={() => setError(null)}>
            {error}
          </Alert>
        </div>
      ) : null}

      <div className="flex flex-wrap gap-6 items-center justify-center max-w-[828px]">
        {sectors.map((sector) => (
          <SectorCard
            key={sector.id}
            sector={sector}
            selected={selected.has(sector.id)}
            onSelect={toggle}
          />
        ))}
      </div>

      <div className="mt-8">
        <Button
          onClick={handleContinue}
          variant="auth"
          size="auth"
          disabled={selected.size === 0 || isContinuing}
          className="w-71 leading-6"
        >
          {isContinuing ? 'Saving...' : 'Continue'}
        </Button>
      </div>
    </div>
  );
};
